<template>
  <div class="input-control">
    <div class="multi-obj-box">
      <div class="main" v-if="!editState" tabindex="-1">
        <div class="items" v-if="value.length">
          <div class="item" v-for="(item,index) in value"
               tabindex="-1" ref="items"
               :key="index">
            <div class="number">
              {{ index + 1 }}
            </div>
            <div class="properties">
            <span class="property" v-for="(field,index) in template" :key="index">
              {{ renderProperty(item[field.field_id], field) }}
            </span>
            </div>
            <div class="more">
              <i class="button edit-button gf gf-edit"
                 @click="enterEditItem(item)"
                 title="Edit item"></i>
              <i class="button delete-button gf gf-delete"
                 @click="tryDeleteItem(item)"
                 title="Delete item"></i>
            </div>
          </div>
        </div>
        <div class="tools">
              <span class="note">
                <template v-if="value.length">
                  {{ value.length }}
                  {{ 'item' | smart_s(value.length) }}
                </template>
                <template v-else>No items</template>
              </span>
          <button class="pure" v-if="canAdd"
                  @click="addNewItem"
          >Add
          </button>
          <span v-else-if="maxItems" class="text-gray">(reached limit)</span>
        </div>
      </div>
      <div class="edit-state" v-else>
        <form-box :fields="template"
                  :initial-values="editingItem"
                  :parent-values="parentValues"
                  :parent-field-id="propertyInForm"
                  @cancel="cancelEditState"
                  @submit="onEditSubmit"
        ></form-box>
      </div>
    </div>
  </div>
</template>

<script>
import InputControlBase from "@/InputControls/InputControlBase";
import {renderValueInCandidateItem} from "@/utils";
const FormBox = () => import("@/InputControls/FormBox");

export default {
  name: "MultiObjBox",
  components: {FormBox},
  mixins: [InputControlBase],
  props: {
    maxItems: Number,
    template: Array,
  },
  created() {
    if (!Array.isArray(this.value)) {
      this.value = [];
    }
  },
  methods: {
    repaintUI() {

    },
    setInputValue(value) {
      if (!Array.isArray(value)) {
        throw new Error('the value should be an array:' + value);
      }
      this.value = value;
      this.cancelEditState();
    },
    focus() {

    },
    renderProperty(value, field) {
      const maxTextLen = 80;
      let text = renderValueInCandidateItem(value, field) || '-';
      if (text.length > maxTextLen) {
        return text.substr(0, maxTextLen) + '...';
      }
      return text;
    },
    addNewItem() {
      this.addingNew = true;
    },
    cancelEditState() {
      if (this.addingNew) {
        this.addingNew = false;
      } else if (this.editingItem) {
        this.editingItem = null;
      }
    },
    onEditSubmit(item) {
      if (this.addingNew) {
        this.value.push(item);
        this.addingNew = false;
        this.$nextTick(() => {
          this.highlightItem(this.value.length - 1);
        });
      } else if (this.editingItem) {
        const index = this.value.indexOf(this.editingItem);
        this.value.splice(index, 1, item);
        this.editingItem = null;
        this.$nextTick(() => {
          this.highlightItem(index);
        });
      }
    },
    enterEditItem(item) {
      this.editingItem = item;
    },
    tryDeleteItem(item) {
      if (confirm('Are you sure you want to delete the item?')) {
        this.value.splice(this.value.indexOf(item), 1);
      }
    },
    highlightItem(index) {
      this.$refs.items[index].focus();
    },
  },
  data() {
    return {
      addingNew: false,
      editingItem: null,
    }
  },
  computed: {
    editState() {
      return this.addingNew || this.editingItem !== null;
    },
    canAdd() {
      return typeof this.maxItems !== 'number' || this.value.length < this.maxItems;
    }
  }
}
</script>

<style scoped lang="scss">
@import "input-controls";
@import "../assets/variables";

.multi-obj-box {
  .main {
    border: 1px solid lightgray;
    border-radius: 4px;
    line-height: 20px;
    background-color: white;
    padding: 6px 6px;

    &:focus {
      box-shadow: 0 0 0 3px rgba($primary-blue, 0.6);
      outline: none;
    }

    .note {
      font-weight: 600;
      color: gray;
    }
  }
}

.tools {
  text-align: center;
  font-size: .86em;

  button {
    margin-left: 0.75rem;
    color: $primary-blue;

    &:hover {
      color: $primary-blue;
    }
  }
}

.items {
  border-bottom: 1px solid lightgray;
  margin-bottom: 0.5rem;
  padding: 0.25rem 0 0.5rem;

  .item {
    border-radius: 4px;
    padding: 0.25rem 0.5rem;
    display: flex;
    align-items: flex-start;
    position: relative;
    outline: none;

    &:hover {
      background-color: #f0f0f0;

      .more {
        display: flex;
      }

      .properties {
        .property {
          &:not(:last-child) {
            &::after {
              color: #d0d0d0;
            }
          }
        }
      }
    }

    &:focus {
      box-shadow: 0 0 0 3px rgba(lightgray, 0.6);
    }

    .more {
      position: absolute;
      right: 0;
      top: 0;
      width: 80px;
      justify-content: space-around;
      height: 100%;
      align-items: center;
      padding: 0 0.25rem 0 1rem;
      background: linear-gradient(to right, rgba(#f0f0f0, 0.9), rgba(#f0f0f0, 1));
      display: none;
      border-radius: 0 4px 4px 0;
    }

    .delete-button {
      color: red;
      font-size: 0.95em;
    }

    .edit-button {
      color: $primary-blue;
      font-size: 1.1em;
    }

    .number {
      min-width: 1.2rem;
      margin-right: 0.75rem;
      font-weight: bold;
      font-size: 12px;
      color: gray;
      text-align: right;
    }

    &:not(:last-child) {
      margin-bottom: 0.25rem;
    }

    .properties {
      font-size: 14px;

      .property {
        &:not(:last-child) {
          &::after {
            content: ' / ';
            font-weight: bold;
            color: lightgray;
          }
        }
      }
    }
  }
}

.edit-state {
  font-size: 0.86rem;
}
</style>
